import { useMemo, useState } from "react";

type Props = {
  /** Any URL on the site we want a favicon for. */
  url: string;
  size?: number;
  className?: string;
};

/**
 * Small site-icon next to a project entry. Uses DuckDuckGo's favicon
 * service (no tracking, no API key, decent quality) and falls back to a
 * pixel "?" tile when the icon can't be fetched (bad URL, DDG down, ad-
 * blocker, etc.) so the layout never collapses.
 */
export function SiteFavicon({ url, size = 32, className = "" }: Props) {
  const [failed, setFailed] = useState(false);

  const domain = useMemo(() => {
    try {
      return new URL(url).hostname;
    } catch {
      return null;
    }
  }, [url]);

  if (!domain || failed) {
    return (
      <span
        aria-hidden
        className={`pixelated inline-grid place-items-center bg-void text-mystic font-display text-[10px] ${className}`}
        style={{ width: size, height: size }}
      >
        ?
      </span>
    );
  }

  return (
    <img
      src={`https://icons.duckduckgo.com/ip3/${domain}.ico`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      className={`pixelated ${className}`}
      style={{ width: size, height: size, objectFit: "contain" }}
      onError={() => setFailed(true)}
    />
  );
}
